'use client';

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Download, Upload, X } from 'lucide-react';
import { Header } from '@/components/header';
import { MetricsTimeRangeControls } from '@/components/metrics-time-range-controls';
import { MetricsRefreshControls, type MetricsViewActions } from '@/components/metrics-refresh-controls';
import { Sidebar } from '@/components/sidebar';
import { cn } from '@/lib/utils';
import { ChartProvider } from '@/app/rate-limit-metrics/ChartContext';
import { PasteGrafanaRangeButton } from '@/app/rate-limit-metrics/components/PasteGrafanaRangeButton';
import {
  makeInitialRangeState,
  rangeReducer,
  RateLimitMetricsContent,
  type RangeAction,
  type RangeState,
} from '@/app/rate-limit-metrics/page';
import { WorkloadMetricsContent } from '@/app/workload-metrics/page';
import type { MetricsTimeRangeKey } from '@/lib/metrics-time-range';
import {
  downloadMetricsSnapshot,
  METRICS_SNAPSHOT_KIND,
  METRICS_SNAPSHOT_VERSION,
  parseMetricsSnapshotJson,
  type MetricsSnapshot,
  type RateLimitMetricsSnapshot,
  type WorkloadMetricsSnapshot,
} from '@/lib/metrics-snapshot';

type MetricsTab = 'workload' | 'rate-limit';

type StagedRangeState = {
  applied: RangeState;
  draft: RangeState;
};

type StagedRangeAction =
  | { type: 'edit'; action: RangeAction }
  | { type: 'apply' }
  | { type: 'applyImmediately'; action: RangeAction };

type SnapshotRequest = {
  createdAtMs: number;
  activeTab: MetricsTab;
  fromMs: number;
  toMs: number;
  presetLabel: string | null;
};

function stagedRangeReducer(state: StagedRangeState, action: StagedRangeAction): StagedRangeState {
  switch (action.type) {
    case 'edit':
      return { ...state, draft: rangeReducer(state.draft, action.action) };
    case 'apply':
      return { applied: state.draft, draft: state.draft };
    case 'applyImmediately': {
      const next = rangeReducer(state.applied, action.action);
      return { applied: next, draft: next };
    }
  }
}

function rangeDurationMs(range: RangeState): number {
  if (range.toMs !== null) return Math.max(0, range.toMs - range.fromMs);
  const lastFixedRange = range.history[range.historyIndex];
  return lastFixedRange.toMs === null
    ? 15 * 60 * 1000
    : Math.max(0, lastFixedRange.toMs - lastFixedRange.fromMs);
}

export default function MetricsPage() {
  const [tab, setTab] = useState<MetricsTab>('workload');
  const [visitedTabs, setVisitedTabs] = useState<Set<MetricsTab>>(() => new Set(['workload']));
  const [viewActionsByTab, setViewActionsByTab] = useState<Partial<Record<MetricsTab, MetricsViewActions | null>>>({});
  const [workloadSnapshot, setWorkloadSnapshot] = useState<WorkloadMetricsSnapshot | null>(null);
  const [rateLimitSnapshot, setRateLimitSnapshot] = useState<RateLimitMetricsSnapshot | null>(null);
  const [snapshotRequest, setSnapshotRequest] = useState<SnapshotRequest | null>(null);
  const [openedSnapshot, setOpenedSnapshot] = useState<MetricsSnapshot | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const snapshotInputRef = useRef<HTMLInputElement>(null);
  const [ranges, dispatchRanges] = useReducer(
    stagedRangeReducer,
    null,
    () => {
      const initialRange = makeInitialRangeState(Date.now());
      return { applied: initialRange, draft: initialRange };
    },
  );
  const { applied: rangeState, draft: draftRangeState } = ranges;
  const { fromMs, toMs, liveTail, presetLabel } = rangeState;
  const draftCanUndo = draftRangeState.historyIndex > 0;
  const draftCanRedo = draftRangeState.historyIndex < draftRangeState.history.length - 1;
  const hasPendingRangeChanges =
    draftRangeState.fromMs !== fromMs
    || draftRangeState.toMs !== toMs
    || draftRangeState.liveTail !== liveTail
    || draftRangeState.presetLabel !== presetLabel;

  const durationMs = useMemo(() => rangeDurationMs(rangeState), [rangeState]);
  const draftDurationMs = useMemo(() => rangeDurationMs(draftRangeState), [draftRangeState]);
  const draftToDisplayMs = draftRangeState.toMs ?? draftRangeState.fromMs + draftDurationMs;

  const setLive = useCallback((value: boolean) => {
    dispatchRanges({ type: 'edit', action: { type: 'setLiveTail', value } });
  }, []);
  const setFromTo = useCallback((nextFromMs: number, nextToMs: number) => {
    if (nextToMs <= nextFromMs) return;
    dispatchRanges({
      type: 'edit',
      action: { type: 'setFromTo', fromMs: nextFromMs, toMs: nextToMs },
    });
  }, []);
  const applyGrafanaRange = useCallback((nextFromMs: number, nextToMs: number) => {
    if (nextToMs <= nextFromMs) return;
    dispatchRanges({
      type: 'applyImmediately',
      action: { type: 'setFromTo', fromMs: nextFromMs, toMs: nextToMs },
    });
  }, []);
  const setPreset = useCallback((key: MetricsTimeRangeKey) => {
    dispatchRanges({
      type: 'applyImmediately',
      action: {
        type: 'setPreset',
        key,
        ...(openedSnapshot && { now: openedSnapshot.range.toMs }),
      },
    });
  }, [openedSnapshot]);
  const handleZoom = useCallback((nextFromMs: number, nextToMs: number) => {
    dispatchRanges({
      type: 'applyImmediately',
      action: { type: 'zoom', fromMs: nextFromMs, toMs: nextToMs },
    });
  }, []);
  const dispatchAppliedRange = useCallback((action: RangeAction) => {
    dispatchRanges({ type: 'applyImmediately', action });
  }, []);
  const handleWorkloadActionsChange = useCallback((actions: MetricsViewActions | null) => {
    setViewActionsByTab((current) => ({ ...current, workload: actions }));
  }, []);
  const handleRateLimitActionsChange = useCallback((actions: MetricsViewActions | null) => {
    setViewActionsByTab((current) => ({ ...current, 'rate-limit': actions }));
  }, []);
  const activeViewActions = viewActionsByTab[tab] ?? null;

  const openSnapshotFile = useCallback(async (file: File) => {
    try {
      const snapshot = parseMetricsSnapshotJson(await file.text());
      setSnapshotError(null);
      setSnapshotRequest(null);
      setOpenedSnapshot(snapshot);
      setWorkloadSnapshot(snapshot.workload);
      setRateLimitSnapshot(snapshot.rateLimit);
      setTab(snapshot.activeTab);
      setVisitedTabs(new Set<MetricsTab>(['workload', 'rate-limit']));
      dispatchRanges({
        type: 'applyImmediately',
        action: {
          type: 'loadSnapshot',
          fromMs: snapshot.range.fromMs,
          toMs: snapshot.range.toMs,
          presetLabel: snapshot.range.presetLabel,
        },
      });
    } catch (error: unknown) {
      setSnapshotError(error instanceof Error ? error.message : String(error));
    } finally {
      if (snapshotInputRef.current) snapshotInputRef.current.value = '';
    }
  }, []);

  const closeSnapshot = useCallback(() => {
    setOpenedSnapshot(null);
    setSnapshotError(null);
    setWorkloadSnapshot(null);
    setRateLimitSnapshot(null);
    setVisitedTabs(new Set([tab]));
    dispatchRanges({ type: 'applyImmediately', action: { type: 'setPreset', key: '15m' } });
  }, [tab]);

  const requestSnapshot = useCallback(() => {
    const createdAtMs = Date.now();
    const snapshotToMs = toMs ?? createdAtMs;
    setVisitedTabs(new Set<MetricsTab>(['workload', 'rate-limit']));
    setSnapshotRequest({
      createdAtMs,
      activeTab: tab,
      fromMs: toMs === null ? snapshotToMs - durationMs : fromMs,
      toMs: snapshotToMs,
      presetLabel,
    });
  }, [durationMs, fromMs, presetLabel, tab, toMs]);

  useEffect(() => {
    if (snapshotRequest === null || workloadSnapshot === null || rateLimitSnapshot === null) return;
    const workloadMatches = workloadSnapshot.loadedFromMs === snapshotRequest.fromMs
      && workloadSnapshot.loadedToMs === snapshotRequest.toMs;
    const rateLimitMatches = rateLimitSnapshot.loadedFromMs === snapshotRequest.fromMs
      && rateLimitSnapshot.loadedToMs === snapshotRequest.toMs;
    if (!workloadMatches || !rateLimitMatches) return;
    const snapshot = {
      kind: METRICS_SNAPSHOT_KIND,
      version: METRICS_SNAPSHOT_VERSION,
      createdAtMs: snapshotRequest.createdAtMs,
      activeTab: snapshotRequest.activeTab,
      range: {
        fromMs: snapshotRequest.fromMs,
        toMs: snapshotRequest.toMs,
        presetLabel: snapshotRequest.presetLabel,
      },
      workload: workloadSnapshot,
      rateLimit: rateLimitSnapshot,
    } as const;
    const timer = window.setTimeout(() => {
      downloadMetricsSnapshot(snapshot);
      setSnapshotRequest(null);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [rateLimitSnapshot, snapshotRequest, workloadSnapshot]);

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        <ChartProvider
          key={openedSnapshot === null ? 'live' : `snapshot-${openedSnapshot.createdAtMs}`}
          onZoomRange={handleZoom}
          initialHiddenPods={openedSnapshot?.rateLimit.hiddenPods}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="px-4 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-xl font-bold">Metrics</h1>
                  <div
                    role="tablist"
                    aria-label="Metric dashboards"
                    className="flex rounded-md border p-0.5"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'workload'}
                      onClick={() => {
                        setTab('workload');
                        setVisitedTabs((current) => new Set(current).add('workload'));
                      }}
                      className={cn(
                        'rounded px-3 py-1 text-xs transition-colors',
                        tab === 'workload' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                      )}
                    >
                      Workload
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'rate-limit'}
                      onClick={() => {
                        setTab('rate-limit');
                        setVisitedTabs((current) => new Set(current).add('rate-limit'));
                      }}
                      className={cn(
                        'rounded px-3 py-1 text-xs transition-colors',
                        tab === 'rate-limit' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                      )}
                    >
                      Rate Limit
                    </button>
                  </div>
                  {openedSnapshot && (
                    <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
                      Snapshot mode · no metric requests
                    </span>
                  )}
                  <input
                    ref={snapshotInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    aria-label="Choose metrics snapshot"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void openSnapshotFile(file);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => snapshotInputRef.current?.click()}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-background px-3 text-xs transition-colors hover:bg-muted"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Open snapshot
                  </button>
                  <button
                    type="button"
                    onClick={requestSnapshot}
                    disabled={snapshotRequest !== null}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-background px-3 text-xs transition-colors hover:bg-muted disabled:cursor-wait disabled:opacity-60"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {snapshotRequest === null ? 'Download snapshot' : 'Preparing snapshot…'}
                  </button>
                  {openedSnapshot && (
                    <button
                      type="button"
                      onClick={closeSnapshot}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-background px-3 text-xs transition-colors hover:bg-muted"
                    >
                      <X className="h-3.5 w-3.5" />
                      Exit snapshot
                    </button>
                  )}
                </div>

                <MetricsTimeRangeControls
                  fromMs={draftRangeState.fromMs}
                  toMs={draftToDisplayMs}
                  onSetFromTo={setFromTo}
                  onSetPreset={setPreset}
                  activePresetLabel={draftRangeState.presetLabel}
                  toDisabled={draftRangeState.liveTail}
                  rangeActions={(
                    <>
                      <button
                        type="button"
                        disabled={!draftCanUndo}
                        onClick={() => dispatchRanges({ type: 'edit', action: { type: 'undo' } })}
                        title="Undo zoom"
                        className="h-7 rounded px-2 text-sm hover:bg-muted disabled:opacity-40"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        disabled={!draftCanRedo}
                        onClick={() => dispatchRanges({ type: 'edit', action: { type: 'redo' } })}
                        title="Redo zoom"
                        className="h-7 rounded px-2 text-sm hover:bg-muted disabled:opacity-40"
                      >
                        →
                      </button>
                    </>
                  )}
                >
                  <PasteGrafanaRangeButton onApply={applyGrafanaRange} setLiveTail={setLive} />
                  {openedSnapshot === null ? (
                    <MetricsRefreshControls
                      label={tab === 'workload' ? 'Auto-refresh' : 'Live tail'}
                      checked={draftRangeState.liveTail}
                      onCheckedChange={setLive}
                      loading={activeViewActions?.loading ?? false}
                      onRefresh={() => {
                        if (hasPendingRangeChanges) {
                          dispatchRanges({ type: 'apply' });
                        } else {
                          activeViewActions?.refresh();
                        }
                      }}
                      disabled={activeViewActions === null}
                      actionLabel="Apply"
                      loadingLabel="Applying…"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => dispatchRanges({ type: 'apply' })}
                      disabled={!hasPendingRangeChanges}
                      className="h-9 rounded-md border bg-background px-3 text-xs hover:bg-muted disabled:opacity-40"
                    >
                      Apply range
                    </button>
                  )}
                </MetricsTimeRangeControls>
              </div>
              {snapshotError && (
                <div role="alert" className="mt-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {snapshotError}
                </div>
              )}
            </div>

            {visitedTabs.has('workload') && (
              <div
                data-testid="workload-metrics-view"
                aria-hidden={tab !== 'workload'}
                className={cn('min-h-0 flex-1', tab === 'workload' ? 'flex' : 'hidden')}
              >
                <WorkloadMetricsContent
                  key={openedSnapshot === null ? 'live-workload' : `snapshot-workload-${openedSnapshot.createdAtMs}`}
                  showHeader={false}
                  onActionsChange={handleWorkloadActionsChange}
                  onSnapshotChange={setWorkloadSnapshot}
                  snapshot={openedSnapshot?.workload}
                  sharedRange={{
                    fromMs: snapshotRequest?.fromMs ?? fromMs,
                    toMs: snapshotRequest?.toMs ?? toMs,
                    durationMs: snapshotRequest === null
                      ? durationMs
                      : snapshotRequest.toMs - snapshotRequest.fromMs,
                    presetLabel,
                    live: snapshotRequest === null && tab === 'workload' && liveTail,
                    setLive,
                  }}
                />
              </div>
            )}
            {visitedTabs.has('rate-limit') && (
              <div
                data-testid="rate-limit-metrics-view"
                aria-hidden={tab !== 'rate-limit'}
                className={cn('min-h-0 flex-1', tab === 'rate-limit' ? 'flex' : 'hidden')}
              >
                <RateLimitMetricsContent
                  key={openedSnapshot === null ? 'live-rate-limit' : `snapshot-rate-limit-${openedSnapshot.createdAtMs}`}
                  fromMs={snapshotRequest?.fromMs ?? fromMs}
                  toMs={snapshotRequest?.toMs ?? toMs}
                  liveTail={snapshotRequest === null && tab === 'rate-limit' && liveTail}
                  presetLabel={presetLabel}
                  canUndo={rangeState.historyIndex > 0}
                  canRedo={rangeState.historyIndex < rangeState.history.length - 1}
                  dispatchRange={dispatchAppliedRange}
                  showHeader={false}
                  onActionsChange={handleRateLimitActionsChange}
                  onSnapshotChange={setRateLimitSnapshot}
                  snapshot={openedSnapshot?.rateLimit}
                />
              </div>
            )}
          </div>
        </ChartProvider>
      </div>
    </div>
  );
}
